import { ReactNode, ComponentProps } from 'react';
import * as Flowbite from 'flowbite-react';
import { SafeProp, TypeProp } from './types';
import { Radio } from './Radio/Radio';
import { Checkbox } from './Checkbox/Checkbox';
import { DateInput } from 'shared/Input/Date/DateInput';
import { TimeInput } from 'shared/Input/Time/TimeInput';
import { TextInput } from 'shared/Input/Text/TextInput';
import { Combo } from 'shared/Input/Combo/Combo';
import { Tag, TagProps } from 'shared/Input/Tag/Tag';
import { ToggleSwitch } from './ToggleSwitch/ToggleSwitch';
import { Slider } from './Slider/Slider';
import { NumberInput } from 'shared/Input/Number/NumberInput';
import { Textarea } from './Textarea/Textarea';
import withClassNames from 'utils/hoc/withClassNames';
import { FileInput } from './File/FileInput';
import { Typeahead } from './Typeahead/Typeahead';

type TypeProps<T> =
    | (ComponentProps<typeof Radio> & TypeProp<'radio'>)
    | (ComponentProps<typeof Checkbox> & TypeProp<'checkbox'>)
    | (ComponentProps<typeof DateInput> & TypeProp<'date'>)
    | (ComponentProps<typeof TimeInput> & TypeProp<'time'>)
    | (ComponentProps<typeof ToggleSwitch> & TypeProp<'toggle'>)
    | (ComponentProps<typeof Slider> & TypeProp<'range'>)
    | (ComponentProps<typeof Combo> & TypeProp<'combo'>)
    | (TagProps<any> & TypeProp<'tag'>)
    | (ComponentProps<typeof NumberInput> & TypeProp<'number'>)
    | (ComponentProps<typeof Textarea> & TypeProp<'area'>)
    | (ComponentProps<typeof FileInput> & TypeProp<'file'>)
    | (ComponentProps<typeof Typeahead<T>> & TypeProp<'typeahead'>)
    | (ComponentProps<typeof TextInput> &
          Partial<TypeProp<'text' | 'password'>>);

type InputProps<T> = {
    type?: SafeProp;
    message?: ReactNode;
} & TypeProps<T>;

const InputWrapper = withClassNames(
    'div',
    'field-wrapper flex flex-col mb-4',
    '[&_label]:whitespace-nowrap'
);

/**
 * Let the user tell us something
 */
export function Input<T = unknown>({ message, ...props }: InputProps<T>) {
    const inputElement = () => {
        switch (props.type) {
            case 'date':
                return <DateInput {...props} />;
            case 'time':
                return <TimeInput {...props} />;
            case 'checkbox':
                return <Checkbox {...props} />;
            case 'radio':
                return <Radio {...props} />;
            case 'toggle':
                return <ToggleSwitch {...props} />;
            case 'range':
                return <Slider {...props} />;
            case 'number':
                return <NumberInput {...props} />;
            case 'combo':
                return <Combo {...props} />;
            case 'area':
                return <Textarea {...props} />;
            case 'file':
                return <FileInput {...props} />;
            case 'tag':
                return <Tag {...props} />;
            case 'typeahead':
                return <Typeahead {...props} />;
            default:
                return <TextInput {...props} />;
        }
    };

    return (
        <InputWrapper className="field-wrapper">
            {inputElement()}

            <Flowbite.HelperText
                className="field-message mt-2 min-h-6"
                color={props.color}
            >
                {message}
            </Flowbite.HelperText>
        </InputWrapper>
    );
}
